import { BackendClient, BlobUploadType } from '~/backend/backend-client';
import {
    createOutageTolerance,
    HttpStatusError,
    MAX_CONCURRENT_UPLOADS,
    POLL_OUTAGE_TOLERANCE_MS,
} from '~/backend/network-retry';
import { isAbortError } from '~/backend/retryOn202';
import { PollData, PollingStatus, ResultID } from '~/backend/types';
import { JobPayload } from '~/state/atoms/jobAtoms';
import { mapWithConcurrency } from '~/utils/concurrency';

/**
 * Uploads a job's files to the backend, {@link MAX_CONCURRENT_UPLOADS} at a
 * time, reporting progress.
 *
 * Each file is sent once. The upload carries no key the backend could
 * recognise a re-send by, so a re-send whose first delivery landed may
 * count as one more file; for a job that declares its file count that
 * would start the task on a scan with a slice missing. A transient failure
 * fails the upload, which the user can retry as a whole.
 *
 * @param jobId - The unique identifier for the job.
 * @param client - The `BackendClient` instance used for file submission.
 * @param payload - The job payload, containing files to upload and any backend-specific parameters.
 * @param onProgressUpdate - Invoked with the count of accepted files as progress is made.
 * @throws When a file was not accepted or the upload was cancelled, once the
 *     uploads already running have settled; no further file is sent, so no
 *     task starts on an incomplete set.
 */
export async function uploadFiles(
    jobId: string,
    client: BackendClient,
    payload: JobPayload,
    onProgressUpdate: (successfullySubmittedCount: number) => void
): Promise<void> {
    let submittedFiles = 0;

    try {
        // Settles only once no upload is running, so a job reported as
        // failed has no uploads still running behind it.
        await mapWithConcurrency(
            payload.filesToUpload,
            MAX_CONCURRENT_UPLOADS,
            async (file: BlobUploadType) => {
                const accepted = await client.submitFile(
                    file,
                    payload.backendParameters
                );
                if (!accepted) {
                    throw new Error('The backend did not accept a file.');
                }
                submittedFiles += 1;
                onProgressUpdate(submittedFiles);
            }
        );
    } catch (error) {
        if (isAbortError(error)) {
            throw new Error(`Uploading cancelled`);
        }
        throw new Error(`Uploading files failed for job ${jobId}`);
    }
}

const DEFAULT_POLLING_INTERVAL = 1000;

/**
 * Periodically polls the backend for job results until they are available.
 * It continues polling at a specified interval until the backend signals that
 * results are ready or if the operation is cancelled.
 * @param jobId - The unique identifier for the job, used for error reporting.
 * @param client - The `BackendClient` instance to use for polling.
 * @param intervalMs - The time in milliseconds between polling attempts.
 * @returns A promise that resolves with `PollData` when the job is complete.
 * @throws Rejects the promise if polling is cancelled, fails with a
 *     non-transient error or error status, or keeps failing transiently
 *     for {@link POLL_OUTAGE_TOLERANCE_MS}.
 */
export async function pollForResults(
    jobId: string,
    client: BackendClient,
    intervalMs: number = DEFAULT_POLLING_INTERVAL
): Promise<PollData> {
    // The backend keeps processing whatever happens to this tab's
    // network, so a blip must not fail the job and strand its result:
    // transient failures keep the loop polling for a bounded outage.
    // `client.poll()` sends a single attempt; this interval is the retry
    // spacing.
    const outage = createOutageTolerance(POLL_OUTAGE_TOLERANCE_MS);
    // Captured once: the client's `abort()` swaps its controller, and this
    // loop must stop on the cancellation of the job it was started for,
    // not read a fresh, never-aborted signal at the next poll.
    const signal = client.signal;
    return new Promise((resolve, reject) => {
        const poll = async () => {
            if (signal?.aborted) {
                outage.end();
                return reject(new DOMException('Task cancelled', 'AbortError'));
            }
            // Only the request is inside the try: the outage tolerance
            // reads a failure's type, and a defect in the handling below
            // must fail the job at once, not be polled through.
            let response: PollData;
            try {
                response = await client.poll();
                if (response.status === PollingStatus.POLLING_FAILED) {
                    // An answer with an error status fails the poll like a
                    // thrown error: a temporary server condition counts
                    // towards the outage, and a definitive one ends it.
                    throw new HttpStatusError(
                        response.httpStatus,
                        'Polling for results'
                    );
                }
            } catch (error) {
                if (isAbortError(error)) {
                    // A cancellation is reported as such, not as a failure.
                    outage.end();
                    reject(error);
                    return;
                }
                if (outage.tolerate(error)) {
                    setTimeout(poll, intervalMs);
                    return;
                }
                reject(new Error(`Job ${jobId}: Polling failed.`));
                return;
            }
            outage.reset();
            if (response.status === PollingStatus.HAS_RESULT) {
                resolve(response);
            } else {
                setTimeout(poll, intervalMs);
            }
        };
        void poll();
    });
}

export type DownloadedResults = {
    files: File[];
    errors: string[];
    downloadErrors: string[];
};

/**
 * Downloads files associated with a result ID and handles any errors that occur during the download.
 * @param client - The `BackendClient` instance used for downloading files.
 * @param resultItem - The result ID containing file paths to download.
 * @returns An object containing the downloaded files and any errors encountered during the process.
 */
async function downloadResultItem(
    client: BackendClient,
    resultItem: ResultID
): Promise<{ files: File[]; errors: string[] }> {
    const downloadedFiles: File[] = [];
    const itemProcessingErrors: string[] = [];

    if (!resultItem.file_ids || resultItem.file_ids.length === 0) {
        return { files: [], errors: [] };
    }

    try {
        const filesFromResultItem: (File | undefined)[] = await Promise.all(
            resultItem.file_ids.map(async (filePath) => {
                try {
                    const blob = await client.downloadFile(
                        resultItem.id,
                        filePath
                    );
                    if (blob) {
                        return new File([blob], filePath || resultItem.id);
                    } else {
                        itemProcessingErrors.push(
                            `No blob for result ${resultItem.id}, path ${filePath}`
                        );
                    }
                } catch (downloadError) {
                    itemProcessingErrors.push(
                        `Failed to download file ${filePath} for result ID ${resultItem.id}.`
                    );
                    return undefined;
                }
            })
        );
        downloadedFiles.push(
            ...filesFromResultItem.filter(
                (file): file is File => file !== undefined
            )
        );
    } catch (error) {
        const errMsg = `Unexpected error processing files for result ID ${resultItem.id}.`;
        itemProcessingErrors.push(errMsg);
    }

    return { files: downloadedFiles, errors: itemProcessingErrors };
}

/**
 * Fetches error information from the backend for a given error ID.
 * After fetching the error, it attempts to delete the corresponding result from the backend.
 * @param client The `BackendClient` instance for API calls.
 * @param errorItem The `ResultID` object that represents a backend error.
 * @returns A promise that resolves to a string containing the error details.
 */
async function downloadErroritem(
    client: BackendClient,
    errorItem: ResultID
): Promise<string | null> {
    try {
        const errorDetails = await client.getErrors(errorItem.id);
        if (errorDetails) {
            return typeof errorDetails === 'string'
                ? errorDetails
                : JSON.stringify(errorDetails);
        }
        client.deleteResult(errorItem.id);
        return `No error details for ID ${errorItem.id}`;
    } catch (getErrorError) {
        return `Failed to retrieve error details for ID ${errorItem.id}`;
    }
}

/**
 * Downloads all results and fetches all error details for a completed job.
 * It processes both successful results and error reports from the backend.
 * @param jobId - The unique identifier for the job, used for logging and error reporting.
 * @param client - The `BackendClient` instance used for downloading files and fetching error details.
 * @param pollData - The data from the polling response, containing arrays of result and error IDs.
 * @returns A promise that resolves to a `DownloadedResults` object, which aggregates all downloaded files, backend error messages, and any errors that occurred during the download process.
 */
export async function downloadBackendResults(
    jobId: string,
    client: BackendClient,
    pollData: PollData
): Promise<DownloadedResults> {
    const allDownloadedFiles: File[] = [];
    const allBackendErrors: string[] = [];
    const allDownloadAndProcessingErrors: string[] = [];

    const resultIds = pollData.result?.result_ids_v2 ?? [];
    if (resultIds.length > 0) {
        const resultOutcomes = await Promise.allSettled(
            resultIds.map((resultItem) =>
                downloadResultItem(client, resultItem)
            )
        );

        resultOutcomes.forEach((outcome) => {
            if (outcome.status === 'fulfilled') {
                allDownloadedFiles.push(...outcome.value.files);
                allDownloadAndProcessingErrors.push(...outcome.value.errors);
            } else {
                allDownloadAndProcessingErrors.push(
                    `Error processing a result item for job ${jobId}. Error: ${outcome.reason?.message || outcome.reason}`
                );
            }
        });
    }

    if (
        pollData.result?.error_ids_v2 &&
        pollData.result.error_ids_v2.length > 0
    ) {
        const errorOutcomes = await Promise.allSettled(
            pollData.result.error_ids_v2.map((errorItem) =>
                downloadErroritem(client, errorItem)
            )
        );

        errorOutcomes.forEach((outcome) => {
            if (outcome.status === 'fulfilled') {
                if (outcome.value) {
                    allBackendErrors.push(outcome.value);
                }
            } else {
                allDownloadAndProcessingErrors.push(
                    `Error processing an error item for job ${jobId}. Error: ${outcome.reason?.message || outcome.reason}`
                );
            }
        });
    }

    // Only clean up results from the backend if all
    // downloads succeeded. This preserves them for retry.
    const allDownloadsSucceeded =
        allDownloadAndProcessingErrors.length === 0 &&
        allBackendErrors.length === 0;
    if (allDownloadsSucceeded) {
        for (const resultItem of resultIds) {
            client.deleteResult(resultItem.id);
        }
    }

    return {
        files: allDownloadedFiles,
        errors: allBackendErrors,
        downloadErrors: allDownloadAndProcessingErrors,
    };
}
